import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useApp } from "../lib/AppContext";
import { formatDateTime } from "../lib/format";
import { Loader2, ShieldCheck, LogOut, KeyRound, Monitor } from "lucide-react";
import SecurityActivity from "./SecurityActivity";

async function ensureStepup(kind = "password", password = null) {
  try {
    if (kind === "password") {
      const res = await api.stepupPassword(password);
      window.__pxos_stepup = res.stepup_token; return true;
    } else {
      const res = await api.stepupOtpVerify(password);  // password here = otp
      window.__pxos_stepup = res.stepup_token; return true;
    }
  } catch (e) { toast.error("Verifica step-up fallita"); return false; }
}

export default function Security() {
  const { user, refresh } = useApp();
  const [sessions, setSessions] = useState([]);
  const [twofa, setTwoFA] = useState(null); // { secret, qr_png_base64 }
  const [enableCode, setEnableCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stepupPwd, setStepupPwd] = useState("");
  const [changePwd, setChangePwd] = useState({ current: "", next: "", next2: "" });

  const load = () => api.sessions().then(setSessions).catch(() => {});
  useEffect(() => { load(); }, []);

  const startTwoFA = async () => {
    if (!await ensureStepup("password", stepupPwd)) return;
    try { setTwoFA(await api.twoFASetup()); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const enableTwoFA = async () => {
    try { const res = await api.twoFAEnable(enableCode); setRecoveryCodes(res.recovery_codes); toast.success("2FA attivata"); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Codice non valido o scaduto"); }
  };
  const disableTwoFA = async () => {
    if (!await ensureStepup("password", stepupPwd)) return;
    try { await api.twoFADisable(); toast.success("2FA disattivata"); await refresh(); setTwoFA(null); }
    catch (e) { toast.error("Errore"); }
  };
  const regenerate = async () => {
    if (!await ensureStepup("password", stepupPwd)) return;
    try { const res = await api.twoFARegenerate(); setRecoveryCodes(res.recovery_codes); }
    catch (e) { toast.error("Errore"); }
  };
  const revokeOthers = async () => {
    try { const r = await api.revokeOthers(); toast.success(`${r.revoked} sessioni revocate`); load(); }
    catch { toast.error("Errore"); }
  };
  const revokeOne = async (id) => { await api.revokeSession(id); load(); };
  const doChangePwd = async (e) => {
    e.preventDefault();
    if (changePwd.next !== changePwd.next2) return toast.error("Le password non coincidono");
    setBusy(true);
    try {
      await api.changePassword(user.must_change_password ? null : changePwd.current, changePwd.next, changePwd.next2);
      toast.success("Password aggiornata");
      setChangePwd({ current: "", next: "", next2: "" });
      await refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="security-page">
      <div>
        <h2 className="font-display text-xl font-semibold">Sicurezza</h2>
        <p className="text-sm opacity-70">Gestisci password, 2FA e dispositivi collegati.</p>
      </div>

      {user?.must_change_password && (
        <div className="pxos-card p-4 border-yellow-300" data-testid="must-change-banner">
          Devi cambiare la password prima di procedere.
        </div>
      )}

      {/* P4 · Attività di accesso */}
      <section className="pxos-card p-6" data-testid="section-activity">
        <SecurityActivity />
      </section>

      {/* Password change */}
      <section className="pxos-card p-6" data-testid="section-change-password">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><KeyRound size={16}/> Cambia password</h2>
        <form onSubmit={doChangePwd} className="mt-4 space-y-3 max-w-md" data-testid="change-password-form">
          {!user?.must_change_password && (
            <label className="block"><span className="text-xs font-semibold uppercase tracking-widest">Password attuale</span>
              <input type="password" required value={changePwd.current} onChange={(e) => setChangePwd({...changePwd, current: e.target.value})} className="pxos-input" data-testid="change-current" /></label>
          )}
          <label className="block"><span className="text-xs font-semibold uppercase tracking-widest">Nuova password (min 12)</span>
            <input type="password" required minLength={12} value={changePwd.next} onChange={(e) => setChangePwd({...changePwd, next: e.target.value})} className="pxos-input" data-testid="change-new" /></label>
          <label className="block"><span className="text-xs font-semibold uppercase tracking-widest">Conferma</span>
            <input type="password" required minLength={12} value={changePwd.next2} onChange={(e) => setChangePwd({...changePwd, next2: e.target.value})} className="pxos-input" data-testid="change-new2" /></label>
          <button disabled={busy} className="pxos-btn-primary" data-testid="change-submit">Aggiorna password</button>
        </form>
      </section>

      {/* 2FA */}
      <section className="pxos-card p-6" data-testid="section-2fa">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><ShieldCheck size={16} /> Verifica in due passaggi (2FA)</h2>
        <div className="mt-3 text-sm opacity-80">Stato: <strong>{user?.totp_enabled ? "Attiva" : "Non attiva"}</strong></div>
        {!user?.totp_enabled && !twofa && (
          <div className="mt-4 max-w-md space-y-3">
            <label className="block text-sm">Inserisci la tua password per procedere:
              <input type="password" value={stepupPwd} onChange={(e) => setStepupPwd(e.target.value)} className="pxos-input mt-1" data-testid="stepup-password" /></label>
            <button className="pxos-btn-primary" onClick={startTwoFA} data-testid="twofa-setup-btn">Configura 2FA</button>
          </div>
        )}
        {twofa && !user?.totp_enabled && (
          <div className="mt-4 space-y-3 max-w-md" data-testid="twofa-setup">
            <img src={`data:image/png;base64,${twofa.qr_png_base64}`} alt="qr" className="w-40 h-40 mx-auto" data-testid="twofa-qr" />
            <div className="text-xs text-center font-mono opacity-70">{twofa.secret}</div>
            <input value={enableCode} onChange={(e) => setEnableCode(e.target.value)} className="pxos-input" placeholder="Codice a 6 cifre" data-testid="twofa-enable-code" />
            <button className="pxos-btn-primary" onClick={enableTwoFA} data-testid="twofa-enable-btn">Attiva 2FA</button>
          </div>
        )}
        {user?.totp_enabled && (
          <div className="mt-4 space-y-3 max-w-md">
            <label className="block text-sm">Inserisci la tua password:
              <input type="password" value={stepupPwd} onChange={(e) => setStepupPwd(e.target.value)} className="pxos-input mt-1" data-testid="stepup-password-off" /></label>
            <div className="flex gap-2">
              <button className="pxos-btn-primary" onClick={regenerate} data-testid="twofa-regen-btn">Rigenera recovery codes</button>
              <button className="rounded-full border px-4 py-2 text-sm" onClick={disableTwoFA} data-testid="twofa-disable-btn">Disattiva 2FA</button>
            </div>
          </div>
        )}
        {recoveryCodes && (
          <div className="mt-4 p-3 rounded-xl bg-yellow-50 text-xs font-mono" data-testid="twofa-recovery-codes">
            <div className="font-sans font-semibold mb-2">Conserva questi codici. Non saranno mostrati di nuovo.</div>
            {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
          </div>
        )}
      </section>

      {/* Sessions */}
      <section className="pxos-card p-6" data-testid="section-sessions">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Monitor size={16} /> Sessioni & dispositivi</h2>
          <button onClick={revokeOthers} className="text-xs underline opacity-80" data-testid="sessions-revoke-others-btn">Disconnetti altri</button>
        </div>
        <ul className="mt-4 divide-y" data-testid="sessions-list">
          {sessions.map((s) => (
            <li key={s.id} className="py-3 flex items-center justify-between gap-4" data-testid="session-row">
              <div className="min-w-0">
                <div className="font-medium">{s.device_name || "Dispositivo"} · {s.browser}</div>
                <div className="text-xs opacity-70">
                  IP {s.ip_address || "—"} · Ultima attività {formatDateTime(s.last_seen_at)} {s.revoked_at ? "· revocata" : ""}
                </div>
              </div>
              {s.is_current ? <span className="pxos-badge" data-testid="session-current-badge">Sessione corrente</span> :
                <button className="text-xs underline" onClick={() => revokeOne(s.id)} data-testid="session-revoke-btn">Revoca</button>}
            </li>
          ))}
          {sessions.length === 0 && <li className="py-4 text-sm opacity-70">Nessuna sessione.</li>}
        </ul>
      </section>
    </div>
  );
}
